import { Component, EventEmitter, Input, OnInit, Optional, Output, Self, ViewChild } from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { MinifiedShape } from '@bciti/types';
import * as uuid from 'uuid';
import { BcitiSelectGridDropdownComponent } from '../select-grid-dropdown/bciti-select-grid-dropdown.component';

export const SHAPE_STYLES: Record<MinifiedShape, object> = {
    [MinifiedShape.CIRCLE]: { 'border-radius': '50%' },
    [MinifiedShape.SQUARE]: { 'border-radius': '3px' },
    [MinifiedShape.TRIANGLE]: { 'clip-path': 'polygon(50% 0%, 0% 100%, 100% 100%)', 'border-radius': '0' },
    [MinifiedShape.RHOMBUS]: { 'clip-path': 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)', 'border-radius': '0' },
    [MinifiedShape.HEXAGON]: { 'clip-path': 'polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%)', 'border-radius': '0' },
    [MinifiedShape.TREE]: { 'clip-path': 'polygon(50% 0%, 72% 40%, 58% 40%, 85% 70%, 60% 70%, 60% 100%, 40% 100%, 40% 70%, 15% 70%, 42% 40%, 28% 40%)', 'border-radius': '0' },
};

@Component({
    selector: 'app-bciti-shape-select',
    templateUrl: './bciti-shape-select.component.html',
    styleUrls: ['./bciti-shape-select.component.scss'],
})
export class BcitiShapeSelectComponent implements OnInit, ControlValueAccessor {
    public onChange: (v?: any) => {};
    public onTouched: () => {};
    public id: string;
    public _value = '';

    @Input() shapes: string[] = [];
    @Input() disabled: boolean;
    @Input() width: string = '80px';
    @Input() validation = true;

    @Output('change') changeOutput = new EventEmitter<string>();
    @Output() blur = new EventEmitter<Event>();

    @ViewChild(BcitiSelectGridDropdownComponent)
    public dropdown: BcitiSelectGridDropdownComponent;

    @Input()
    get value(): string {
        return this._value;
    }

    set value(v: string) {
        this._value = v;
        this.changeOutput.emit(v);
        if (this.onChange) {
            this.onChange(v);
            this.onTouched();
        }
    }

    get invalid() {
        return this.ngControl ? this.ngControl.invalid : false;
    }

    get valid() {
        return this.ngControl ? this.ngControl.valid : false;
    }

    get pristine() {
        return this.ngControl ? this.ngControl.pristine : false;
    }

    get dirty() {
        return this.ngControl ? this.ngControl.dirty : false;
    }

    get touched() {
        return this.ngControl ? this.ngControl.touched : false;
    }

    constructor(@Self() @Optional() private ngControl: NgControl) {
        if (this.ngControl) {
            this.ngControl.valueAccessor = this;
        }
    }

    ngOnInit(): void {
        this.id = uuid.v4();
        if (!this.value && this.ngControl) {
            this.valueChange(MinifiedShape.CIRCLE);
            this.ngControl.control?.markAsPristine();
            this.ngControl.control?.markAsUntouched();
        }
    }

    getShapeStyle(shape: string): object {
        return SHAPE_STYLES[shape as MinifiedShape] ?? {};
    }

    valueChange(v: string): void {
        this.value = v;
    }

    onBlur(event: Event): void {
        this.blur.emit(event);
        if (this.onTouched) {
            this.onTouched();
        }
    }

    writeValue(value: string): void {
        this._value = value;
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    select(shape: string): void {
        this.valueChange(shape);
        this.dropdown.hide();
    }

    showDropdown() {
        if (!this.disabled) {
            this.dropdown.show();
        }
    }

    hideDropdown() {
        this.dropdown.hide();
    }
}
